import React from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { OccurrenceSummary } from '../../types/analytics';

interface TrendSummaryCardsProps {
  summaries: OccurrenceSummary[];
}

interface TrendCardConfig {
  label: string;
  dataKey: 'completionRate' | 'totalResponses' | 'flagsTriggered';
  color: string;
  formatValue: (value: number) => string;
}

const cardClassName =
  'bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700';

const getDelta = (values: number[]): number | undefined => {
  if (values.length < 2) {
    return undefined;
  }

  return values[values.length - 1] - values[values.length - 2];
};

const formatDelta = (delta: number): string => {
  const prefix = delta > 0 ? '+' : '';
  return `${prefix}${delta.toFixed(1)}`;
};

const TrendSummaryCards: React.FC<TrendSummaryCardsProps> = ({ summaries }) => {
  const sortedSummaries = [...summaries].sort((a, b) => a.occurrenceNumber - b.occurrenceNumber);
  const hasFlagData = sortedSummaries.some((summary) => summary.flagsTriggered > 0);

  const chartData = sortedSummaries.map((summary) => ({
    label: `Occ #${summary.occurrenceNumber}`,
    completionRate: summary.completionRate,
    totalResponses: summary.totalResponses,
    flagsTriggered: summary.flagsTriggered,
  }));

  const baseCards: TrendCardConfig[] = [
    {
      label: 'Completion Rate',
      dataKey: 'completionRate',
      color: '#3B82F6',
      formatValue: (value: number) => `${value.toFixed(1)}%`,
    },
    {
      label: 'Response Count',
      dataKey: 'totalResponses',
      color: '#10B981',
      formatValue: (value: number) => Math.round(value).toString(),
    },
    {
      label: 'Flags Triggered',
      dataKey: 'flagsTriggered',
      color: '#F59E0B',
      formatValue: (value: number) => Math.round(value).toString(),
    },
  ];

  const cards = baseCards.filter((card) => (card.dataKey === 'flagsTriggered' ? hasFlagData : true));

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {cards.map((card) => {
        const values = chartData.map((point) => Number(point[card.dataKey]));
        const currentValue = values[values.length - 1] ?? 0;
        const delta = getDelta(values);
        const isUp = (delta ?? 0) > 0;
        const isDown = (delta ?? 0) < 0;

        return (
          <div key={card.dataKey} className={cardClassName}>
            <p className="text-sm text-gray-600 dark:text-gray-400">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
              {card.formatValue(currentValue)}
            </p>

            <div className="h-10 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <Area
                    type="monotone"
                    dataKey={card.dataKey}
                    stroke={card.color}
                    fill={card.color}
                    fillOpacity={0.2}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {delta !== undefined && (
              <p
                className={`mt-2 text-sm font-medium ${
                  isUp
                    ? 'text-green-600 dark:text-green-400'
                    : isDown
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {isUp ? '↑' : isDown ? '↓' : '→'} {formatDelta(delta)} vs previous
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TrendSummaryCards;
